// region.service.ts

import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { Region } from './region.model';
import {Organization} from "../organization/organization.model";
import {FetchOrganizationsResponse} from "../organization/organization.service";

// Example response interfaces
interface FetchRegionsResponse {
  data: {
    regions: Region[];
  };
}

interface CreateRegionResponse {
  data: Region;
}

interface UpdateRegionResponse {
  data: Region;
}

interface FetchRegionByIdResponse {
  data: Region;
}

@Injectable({
  providedIn: 'root',
})
export class RegionService {
  constructor(private apiService: ApiService) {}

  /**
   * Fetches all regions.
   * Returns Region[].
   */
  async fetchAll(): Promise<Region[]> {
    try {
      // GET /v1/secure/region
      const response = await this.apiService.get<FetchRegionsResponse>('/region');
      // Return the actual array: response.data.regions
      return response.data.regions;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Creates a new region.
   * Returns a single Region.
   */
  async create(regionData: Region): Promise<Region> {
    try {
      // POST /v1/secure/region
      const response = await this.apiService.post<CreateRegionResponse>('/region', regionData);
      // Return the single Region object:
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Updates an existing Region by ID.
   */
  async update(regionData: Region): Promise<Region> {
    // PUT /region/{regionId}
    const response = await this.apiService.put<UpdateRegionResponse>(`/region/${regionData.id}`, regionData);
    return response.data;
  }

  /**
   * Deletes a Region by ID.
   */
  async delete(regionId: string): Promise<void> {
    // DELETE /region/{regionId}
    await this.apiService.delete(`/region/${regionId}`);
    // No content to return if your backend uses a 204 or 200 success response
    return Promise.resolve();
  }

  /**
   * Fetches a single region by its ID.
   * Returns a single Region.
   */
  async fetchById(regionId: string): Promise<Region> {
    try {
      // GET /v1/secure/region/{regionId}
      const response = await this.apiService.get<FetchRegionByIdResponse>(`/region/${regionId}`);
      // Return the single Region object:
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches all organizations for region ID.
   */
  async fetchAllForRegionId(regionId: String): Promise<Organization[]> {
    try {
      // GET /v1/secure/{regionId}/organizations
      const response = await this.apiService.get<FetchOrganizationsResponse>(`/region/${regionId}/organizations`);
      return response.data.organizations;
    } catch (error) {
      throw error;
    }
  }
}
